<template>
  <StandardCard
    @action-clicked="handleCardAction"
    :actions="actions"
    :processing="processing"
    data-testid="business-billing-card"
    title="Billing information"
    border
  >
    <v-row>
      <LabeledTextfield
        v-for="field in FIELDS"
        v-model="business[field.field]"
        :key="field.field"
        :cols="field.cols"
        :field="field.field"
        :readonly="!isEditor"
        :schema-id="business.schema_id"
      />

      <LabeledSwitch
        v-if="isPaymentsFeatureEnabled"
        v-model="business.enable_payments"
        subtitle="If enabled, providers will be able to send scheduled and completed subsidy payments from their dashboard."
        title="Enable payments"
      />
    </v-row>
  </StandardCard>

  <ConfirmDialog ref="confirmDialog" />
</template>

<script setup>
import { useStore } from 'vuex';

import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import LabeledSwitch from '@/shared/components/form/LabeledSwitch.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import useEventBus from '@/shared/composables/useEventBus';
import useRole from '@/shared/composables/useRole';

const FIELDS = [
  { field: 'billing_contact_first_name', cols: '6' },
  { field: 'billing_contact_last_name', cols: '6' },
  { field: 'billing_contact_email', cols: '6' },
  { field: 'billing_contact_phone', cols: '6' },
  { field: 'billing_address1', cols: '12' },
  { field: 'billing_address2', cols: '12' },
  { field: 'billing_city', cols: '6' },
  { field: 'billing_state', cols: '6' },
  { field: 'billing_zip', cols: '6' },
];

const eventBus = useEventBus();
const { isSpecialist } = useRole();
const store = useStore();

const props = defineProps({
  isEditor: {
    type: Boolean,
    default: false,
  },
  businessApi: {
    type: Object,
    default: () => ({}),
  },
});

const business = defineModel({ type: Object, required: true });

const emit = defineEmits(['updated']);

const confirmDialog = ref(null);
const processing = ref(false);

const actions = computed(() => {
  if (!props.isEditor) return [];

  return [
    {
      title: 'Save',
      event: 'save',
    },
  ];
});

const isPaymentsFeatureEnabled = computed(() => {
  return store.state.features?.enable_payments && isSpecialist;
});

async function handleCardAction({ action }) {
  switch (action) {
    case 'save':
      return await saveBillingInfo();
  }
}

async function saveBillingInfo() {
  if (!props.isEditor) return;

  // Excludes bank account data
  const billingData = {
    billing_contact_first_name: business.value.billing_contact_first_name,
    billing_contact_last_name: business.value.billing_contact_last_name,
    billing_contact_email: business.value.billing_contact_email,
    billing_contact_phone: business.value.billing_contact_phone,
    billing_address1: business.value.billing_address1,
    billing_address2: business.value.billing_address2,
    billing_city: business.value.billing_city,
    billing_state: business.value.billing_state,
    billing_zip: business.value.billing_zip,
    enable_payments: business.value.enable_payments,
  };

  processing.value = true;

  const response = await props.businessApi.update(business.value.id, billingData);

  processing.value = false;

  if (response?.status !== 200) return;

  emit('updated', response.data);
  eventBus.chime('Saved');
}
</script>
